'use client';
import React, { useState } from 'react';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort, fmtNum } from '@/lib/financial-model';
import { Info } from 'lucide-react';

type DisplayMode = 'crores' | 'absolute' | 'lakhs';

function formatVal(val: number, mode: DisplayMode): string {
  if (val === 0) return '—';
  if (mode === 'crores') return fmtCrShort(val);
  if (mode === 'lakhs') {
    const l = val / 100000;
    return `₹${l.toFixed(1)}L`;
  }
  return `₹${fmtNum(Math.round(val))}`;
}

interface PnLRow {
  id: string;
  label: string;
  isSection?: boolean;
  isHighlight?: boolean;
  isMargin?: boolean;
  isNegative?: boolean;
  values: (number | string)[];
  format?: 'currency' | 'percent' | 'number';
  indent?: boolean;
}

export default function PnLTable() {
  const { model } = useModel();
  const [displayMode, setDisplayMode] = useState<DisplayMode>('crores');

  const years = model.years;

  function pct(val: number): string {
    return val === 0 ? '—' : `${(val * 100).toFixed(1)}%`;
  }

  function cur(val: number): string {
    return formatVal(val, displayMode);
  }

  const rows: PnLRow[] = [
    {
      id: 'production',
      label: 'Production (Tonnes)',
      format: 'number',
      values: years.map((y) => (y.production === 0 ? '—' : fmtNum(y.production))),
    },
    {
      id: 'utilisation',
      label: 'Utilisation %',
      format: 'percent',
      values: years.map((y) => (y.utilisation === 0 ? '—' : pct(y.utilisation))),
    },
    { id: 'sep1', label: '', isSection: true, values: [] },
    {
      id: 'revenue',
      label: 'Pellet Sales Revenue',
      isHighlight: false,
      values: years.map((y) => y.revenue),
      format: 'currency',
    },
    { id: 'sep2', label: 'Variable Costs', isSection: true, values: [] },
    {
      id: 'rawmat',
      label: 'Raw Material Cost',
      indent: true,
      values: years.map((y) => y.rawMaterialCost),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'power',
      label: 'Power (Grid — No Solar)',
      indent: true,
      values: years.map((y) => y.powerCost),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'labourmaint',
      label: 'Labour + Maint + Pack + Other',
      indent: true,
      values: years.map((y) => y.labourMaintPackOther),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'totalvc',
      label: 'Total Variable Cost',
      isHighlight: true,
      values: years.map((y) => y.totalVariableCost),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'grossprofit',
      label: 'Gross Profit',
      isHighlight: true,
      values: years.map((y) => y.grossProfit),
      format: 'currency',
    },
    { id: 'sep3', label: 'Fixed Costs', isSection: true, values: [] },
    {
      id: 'landlease',
      label: 'Land Lease',
      indent: true,
      values: years.map((y) => y.landLease),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'salaries',
      label: 'Salaries & Admin',
      indent: true,
      values: years.map((y) => y.salaries),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'insurance',
      label: 'Insurance, Security & Misc',
      indent: true,
      values: years.map((y) => y.insuranceMisc),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'totalfc',
      label: 'Total Fixed Cost (ex-Dep)',
      isHighlight: true,
      values: years.map((y) => y.totalFixedCost),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'ebitda',
      label: 'EBITDA',
      isHighlight: true,
      values: years.map((y) => y.ebitda),
      format: 'currency',
    },
    {
      id: 'dep',
      label: 'Depreciation (SLM 10 yrs on ₹4.40 Cr)',
      indent: true,
      values: years.map((y) => y.depreciation),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'ebit',
      label: 'EBIT',
      isHighlight: true,
      values: years.map((y) => y.ebit),
      format: 'currency',
    },
    {
      id: 'interest',
      label: 'Interest on Debt',
      indent: true,
      values: years.map((y) => y.interest),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'pbt',
      label: 'Profit Before Tax (PBT)',
      isHighlight: true,
      values: years.map((y) => y.pbt),
      format: 'currency',
    },
    {
      id: 'tax',
      label: `Tax (${(model.assumptions.taxRate * 100).toFixed(0)}%)`,
      indent: true,
      values: years.map((y) => y.tax),
      format: 'currency',
      isNegative: true,
    },
    {
      id: 'pat',
      label: 'PROFIT AFTER TAX (PAT)',
      isHighlight: true,
      values: years.map((y) => y.pat),
      format: 'currency',
    },
    { id: 'sep4', label: 'Margin Analysis', isSection: true, values: [] },
    {
      id: 'grossmargin',
      label: 'Gross Margin %',
      isMargin: true,
      values: years.map((y) => (y.grossMarginPct === 0 ? '—' : pct(y.grossMarginPct))),
      format: 'percent',
    },
    {
      id: 'ebitdamargin',
      label: 'EBITDA Margin %',
      isMargin: true,
      values: years.map((y) => (y.ebitdaMarginPct === 0 ? '—' : pct(y.ebitdaMarginPct))),
      format: 'percent',
    },
    {
      id: 'patmargin',
      label: 'PAT Margin %',
      isMargin: true,
      values: years.map((y) => (y.patMarginPct === 0 ? '—' : pct(y.patMarginPct))),
      format: 'percent',
    },
  ];

  const colHeaders = [
    'Particulars',
    'Yr 0 (Const)',
    ...Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`),
  ];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Profit & Loss Statement — 5 TPH Lean Base Case — No Solar — 10 Year
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            All values in ₹ · Formulas preserved from Excel model
          </p>
        </div>
        <div className="flex gap-2">
          {(['crores', 'lakhs', 'absolute'] as DisplayMode[]).map((mode) => (
            <button
              key={`display-mode-${mode}`}
              onClick={() => setDisplayMode(mode)}
              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all duration-150
                ${
                  displayMode === mode
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                }`}
            >
              {mode === 'crores' ? '₹ Crores' : mode === 'lakhs' ? '₹ Lakhs' : '₹ Absolute'}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs border-collapse" style={{ minWidth: '900px' }}>
            <thead>
              <tr className="bg-muted border-b border-border">
                {colHeaders.map((h, idx) => (
                  <th
                    key={`pnl-header-${idx}`}
                    className={`py-3 px-3 font-semibold text-muted-foreground text-xs tracking-wide
                      ${idx === 0 ? 'text-left sticky-col min-w-[200px]' : 'text-right min-w-[90px] whitespace-nowrap'}
                      ${idx === 1 ? 'bg-muted/80' : ''}
                    `}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                if (row.isSection) {
                  return (
                    <tr
                      key={`pnl-section-${row.id}`}
                      className="section-header-row border-b border-border"
                    >
                      <td colSpan={12} className="py-2 px-3 text-xs uppercase tracking-widest">
                        {row.label}
                      </td>
                    </tr>
                  );
                }

                const isHighlight = row.isHighlight;
                const isMargin = row.isMargin;

                return (
                  <tr
                    key={`pnl-row-${row.id}`}
                    className={`border-b border-border/50 hover:bg-muted/30 transition-colors
                      ${isHighlight ? 'highlight-row' : ''}
                      ${isMargin ? 'bg-info-bg' : ''}
                    `}
                  >
                    <td
                      className={`py-2.5 px-3 sticky-col font-medium
                      ${isHighlight ? 'font-semibold text-foreground' : 'text-foreground'}
                      ${row.indent ? 'pl-6 text-muted-foreground font-normal' : ''}
                      ${isMargin ? 'text-accent font-semibold' : ''}
                    `}
                    >
                      {row.label}
                    </td>
                    {years.map((y, yIdx) => {
                      const rawVal = row.values[yIdx];
                      const isString = typeof rawVal === 'string';
                      const numVal = isString ? 0 : (rawVal as number);

                      let displayStr: string;
                      if (isString) {
                        displayStr = rawVal as string;
                      } else if (row.format === 'currency') {
                        displayStr = cur(numVal);
                      } else if (row.format === 'percent') {
                        displayStr = numVal === 0 ? '—' : pct(numVal);
                      } else {
                        displayStr = numVal === 0 ? '—' : fmtNum(numVal);
                      }

                      const isNegativeVal = !isString && numVal < 0;
                      const isZeroYear = y.year === 0;

                      return (
                        <td
                          key={`pnl-cell-${row.id}-yr${yIdx}`}
                          className={`py-2.5 px-3 text-right table-cell-mono
                            ${isNegativeVal ? 'negative-value' : ''}
                            ${isHighlight && !isNegativeVal ? 'font-semibold' : ''}
                            ${isZeroYear ? 'text-muted-foreground bg-muted/20' : ''}
                            ${isMargin ? 'text-accent font-semibold' : ''}
                          `}
                        >
                          {displayStr}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Note */}
      <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
        <Info size={13} className="text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          Revenue escalation applied from Year 2 at{' '}
          {(model.assumptions.priceEscalation * 100).toFixed(1)}% p.a. · Fixed costs inflated at{' '}
          {(model.assumptions.inflationFixedCosts * 100).toFixed(1)}% p.a. from Year 2 ·
          Depreciation SLM on ₹{fmtCrShort(model.depreciationBase)} over{' '}
          {model.assumptions.depreciationPeriod} years · Tax = MAX(0, PBT ×{' '}
          {(model.assumptions.taxRate * 100).toFixed(0)}%)
        </p>
      </div>
    </div>
  );
}
